import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { fetchUsers } from 'store/thunks/adminUsersThunk';
import { IAdminUser } from 'types';
import { ESliceDataFetchingStatus } from 'types/common';

export interface IAdminUsersState {
  users: IAdminUser[];
  status: ESliceDataFetchingStatus;
  errorMessage: string | null;
  pagesQuantity: number;
  itemsPerPage: number;
  currentPage: number;
  sortBy: string | null;
  sortDir: string | null;
  search: string | null;
  vip: string | null;
  weeklyFrom: string | null;
  weeklyTo: string | null;
  dailyFrom: string | null;
  dailyTo: string | null;
  createdFromDate: string | null;
  createdToDate: string | null;
  updatedFromDate: string | null;
  updatedToDate: string | null;
}

const initialState: IAdminUsersState = {
  users: [],
  status: ESliceDataFetchingStatus.initial,
  pagesQuantity: 1,
  itemsPerPage: 10,
  currentPage: 0,
  errorMessage: null,
  sortBy: null,
  sortDir: null,
  search: null,
  vip: null,
  weeklyFrom: null,
  weeklyTo: null,
  dailyFrom: null,
  dailyTo: null,
  createdFromDate: null,
  createdToDate: null,
  updatedFromDate: null,
  updatedToDate: null,
};

export const adminUsersSlice = createSlice({
  name: 'adminUsers',
  initialState,
  reducers: {
    usersListSetPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setItemsPerUsersListPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
      state.currentPage = initialState.currentPage;
    },
    setSort: (
      state,
      action: PayloadAction<{
        sortBy: IAdminUsersState['sortBy'];
        sortDir: IAdminUsersState['sortDir'];
      }>,
    ) => {
      state.sortBy = action.payload.sortBy;
      state.sortDir = action.payload.sortDir;
    },
    setSearch: (state, action: PayloadAction<IAdminUsersState['search']>) => {
      state.search = action.payload;
    },
    setVipStatusFilter: (state, action: PayloadAction<IAdminUsersState['vip']>) => {
      state.vip = action.payload;
    },
    setAverageDailyHashrateFilter: (
      state,
      action: PayloadAction<{
        fromValue: IAdminUsersState['dailyFrom'];
        toValue: IAdminUsersState['dailyTo'];
      }>,
    ) => {
      state.dailyFrom = action.payload.fromValue;
      state.dailyTo = action.payload.toValue;
    },
    setAverageWeeklyHashrateFilter: (
      state,
      action: PayloadAction<{
        fromValue: IAdminUsersState['weeklyFrom'];
        toValue: IAdminUsersState['weeklyTo'];
      }>,
    ) => {
      state.weeklyFrom = action.payload.fromValue;
      state.weeklyTo = action.payload.toValue;
    },
    setCreatedAtDateFilter: (
      state,
      action: PayloadAction<{
        fromDate: IAdminUsersState['createdFromDate'];
        toDate: IAdminUsersState['createdToDate'];
      }>,
    ) => {
      state.createdFromDate = action.payload.fromDate;
      state.createdToDate = action.payload.toDate;
    },
    setUpdatedAtDateFilter: (
      state,
      action: PayloadAction<{
        fromDate: IAdminUsersState['updatedFromDate'];
        toDate: IAdminUsersState['updatedToDate'];
      }>,
    ) => {
      state.updatedFromDate = action.payload.fromDate;
      state.updatedToDate = action.payload.toDate;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchUsers.fulfilled, (state, { payload }) => {
        state.status = ESliceDataFetchingStatus.success;
        state.users = payload.content;
        state.pagesQuantity = payload.details.totalPages;
      })
      .addCase(fetchUsers.pending, (state) => {
        state.status = ESliceDataFetchingStatus.loading;
        state.errorMessage = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = ESliceDataFetchingStatus.error;
        state.errorMessage = action.error.message || 'unknown fetch users list error';
      }),
});

export const selectAdminUsers = (state: RootState) => state.adminUsers;

export const {
  usersListSetPage,
  setItemsPerUsersListPage,
  setSort,
  setSearch,
  setVipStatusFilter,
  setAverageDailyHashrateFilter,
  setAverageWeeklyHashrateFilter,
  setCreatedAtDateFilter,
  setUpdatedAtDateFilter,
} = adminUsersSlice.actions;

export const adminUsersReducer = adminUsersSlice.reducer;
